import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, IdControl, ScoreCards } from '@shared-ui/components';
import type { ColumnDef, SortState } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';
import type { LedgerQuestion, QSeverity, QualityRuleInfo, QuestionTotals, RuleCounts } from '../../../api/questionQualityApi';
import { questionQualityApi, IMPORT_STATUS_LABEL, type ImportStatus } from '../../../api/questionQualityApi';
import { ResolutionBadge } from './ResolutionEditor';
import { findingsPath, sevColor, sevRank, verdictColor } from './verdictDisplay';
import './QuestionQuality.css';

/**
 * Question Quality → By Rule. The rule is the row: how each check scores over the
 * questions currently in scope, expanding into what it asks and what it found.
 * Answers "which check is our weakest?" — its sibling ByQuestion answers "which
 * question is worst?" over the very same rows.
 *
 * It counts what it is GIVEN. The filter bar above the tabs decides the
 * population, so narrowing to one topic or one grade re-scores every rule against
 * that slice — which is only possible because both views now read one ledger.
 */

interface RuleRow {
    ruleId: string;
    info?: QualityRuleInfo;
    pass: number;
    unsure: number;
    fail: number;
    failBySeverity: Record<QSeverity, number>;
}

function SevPills({ c }: { c: Record<QSeverity, number> }) {
    return (
        <span style={{ display: 'inline-flex', gap: 6 }}>
            {(['critical', 'major', 'minor'] as const).map(s => (
                <span key={s} title={s} style={{
                    fontVariantNumeric: 'tabular-nums', color: c[s] ? sevColor[s] : '#bbb', fontWeight: c[s] ? 700 : 400,
                }}>{c[s]}</span>
            ))}
        </span>
    );
}

/**
 * What the rule asks, at the head of its expansion.
 *
 * `summary` is the sentence a reader wants; `rubric` below it is the judge's
 * PROMPT — instructions, not documentation — so it is labelled and collapsed.
 */
function RuleRubric({ info }: { info: QualityRuleInfo }) {
    const { subjects, questionTypes, requires } = info.applies;
    return (
        <div className="qq-rubric">
            <p className="qq-rubric-summary">{info.summary}</p>
            <div className="qq-rubric-meta">
                <b>v{info.version}</b>
                <span>{info.severity}</span>
                <span>{subjects?.length ? subjects.join(', ') : 'every subject'}</span>
                <span>{questionTypes?.length ? questionTypes.join(', ') : 'every question type'}</span>
                {requires?.length ? <span>needs {requires.join(', ')}</span> : null}
            </div>
            <details className="qq-rubric-details">
                <summary>What the judge is told</summary>
                <p className="qq-rubric-body">{info.rubric}</p>
            </details>
        </div>
    );
}

/**
 * The questions this rule found something in, worst first.
 *
 * Fetched on expand, with the current filters plus this rule, rather than sifted
 * from rows already on the page: the page is fifty questions and this list is
 * "every question in scope that this rule flagged". Those were the same thing
 * only while the browser held the whole subject.
 */
const DRILL_PAGE = 25;

function RuleFindings({ rule, subject, filters }: {
    rule: string; subject: string; filters: Record<string, string>;
}) {
    const [rows, setRows] = useState<LedgerQuestion[] | null>(null);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState<string | null>(null);

    const filterKey = JSON.stringify(filters);
    useEffect(() => {
        let live = true;
        setRows(null); setError(null);
        questionQualityApi.ledger(subject, {
            pageSize: DRILL_PAGE,
            filters: { ...(JSON.parse(filterKey) as Record<string, string>), rule },
        })
            .then(r => { if (live) { setRows(r.questions); setTotal(r.total); } })
            .catch(e => { if (live) setError(e instanceof Error ? e.message : 'Failed to load'); });
        return () => { live = false; };
    }, [subject, rule, filterKey]);

    const found = useMemo(() => (rows ?? [])
        .map(q => ({ q, cell: q.cells[rule] }))
        .filter((x): x is { q: LedgerQuestion; cell: NonNullable<typeof x.cell> } =>
            !!x.cell && x.cell.verdict !== 'pass')
        .sort((a, b) => (b.cell.verdict === 'fail' ? sevRank[b.cell.severity] : 0)
            - (a.cell.verdict === 'fail' ? sevRank[a.cell.severity] : 0)),
    [rows, rule]);

    if (error) return <div className="qq-detail qq-warn">{error}</div>;
    if (rows === null) return <div className="qq-detail qq-dim">Loading…</div>;
    if (!found.length) {
        return <div className="qq-detail qq-dim">Nothing open for this rule in the current scope.</div>;
    }

    return (
        <div className="qq-detail">
            <table className="dt dt--bare dt--compact">
                <thead><tr>
                    <th style={{ textAlign: 'left', width: '32%' }}>
                        Question{total > found.length ? ` (${found.length} of ${total})` : ''}
                    </th>
                    <th style={{ textAlign: 'left', width: 100 }}>Verdict</th>
                    <th style={{ textAlign: 'left' }}>Why</th>
                    <th style={{ textAlign: 'left', width: '20%' }}>Resolution</th>
                </tr></thead>
                <tbody>
                    {found.map(({ q, cell }) => (
                        <tr key={q.questionId}>
                            <td style={{ textAlign: 'left' }}>
                                <div className="qq-qcell">
                                    <IdControl value={q.questionId} container="questions" />
                                    {q.questionText && (
                                        <span className="qq-qtext">
                                            <LatexText text={q.questionText} truncate={160} />
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td style={{
                                color: cell.verdict === 'fail' ? sevColor[cell.severity] : verdictColor[cell.verdict],
                                fontWeight: 600,
                            }}>
                                {cell.verdict === 'fail' ? `fail · ${cell.severity}` : cell.verdict}
                            </td>
                            <td style={{ textAlign: 'left', whiteSpace: 'normal' }}>
                                {cell.rationale ? <LatexText text={cell.rationale} /> : '—'}
                            </td>
                            <td style={{ textAlign: 'left' }}>
                                {/* The badge, not the editor: resolving happens on
                                    the question's own page, where the rationale it
                                    answers is readable. */}
                                <span className="qq-idrow">
                                    <ResolutionBadge status={cell.resolution?.status ?? 'active'} />
                                    <Link className="cs-link" to={findingsPath(q.questionId, subject)}>Open →</Link>
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function ByRule({ counts, totals, poolTotal, ruleInfo, subject, filters }: {
    /** Tallied by the SERVER over every question the filters matched. Counting
     *  them here meant counting whatever rows had been sent, which was a page. */
    counts: RuleCounts[];
    totals: QuestionTotals;
    /** The POOL: every question this subject has at the chosen board and grade,
     *  judged or not. The denominator `totals.checked` is a fraction of — and a
     *  different population from it, which is the point. It used to be the
     *  ledger's own `total`, a number identical to `checked` by construction, so
     *  the page reported full coverage of a corpus it had only partly seen. */
    poolTotal: number;
    ruleInfo: QualityRuleInfo[];
    subject: string;
    /** Carried into the drill-down so it asks the same question the table did. */
    filters: Record<string, string>;
}) {
    const [sort, setSort] = useState<SortState>({ sortBy: 'fail', sortOrder: 'desc' });
    const infoById = useMemo(() => new Map(ruleInfo.map(r => [r.ruleId, r])), [ruleInfo]);

    const ruleRows: RuleRow[] = useMemo(
        () => counts.map(c => ({ ...c, info: infoById.get(c.ruleId) })),
        [counts, infoById],
    );
    const verdicts = useMemo(() => ruleRows.reduce((acc, r) => ({
        total: acc.total + r.pass + r.unsure + r.fail,
        pass: acc.pass + r.pass,
        unsure: acc.unsure + r.unsure,
        critical: acc.critical + r.failBySeverity.critical,
        major: acc.major + r.failBySeverity.major,
        minor: acc.minor + r.failBySeverity.minor,
    }), { total: 0, pass: 0, unsure: 0, critical: 0, major: 0, minor: 0 }), [ruleRows]);

    /**
     * What the pool count is narrowed BY, said out loud under the number.
     *
     * Only board, grade and import status reach it — the rest of the bar
     * filters verdicts, not questions — so a reader who has set a topic and a
     * severity needs telling that this one card did not move. Import status
     * DOES reach it: it is a question-level fact like board/grade, not a
     * per-check verdict, so a Draft-only pick has to narrow the pool too.
     */
    const poolScope = useMemo(() => {
        const parts = [
            filters.board ? filters.board.toUpperCase() : null,
            filters.grade ? `Grade ${filters.grade}` : null,
            filters.importStatus ? IMPORT_STATUS_LABEL[filters.importStatus as ImportStatus] : null,
        ].filter(Boolean);
        return parts.length ? `in ${parts.join(' · ')}` : 'every board and grade';
    }, [filters.board, filters.grade, filters.importStatus]);

    /** How much of that pool has ever been judged — the fraction the two cards
     *  exist to state. Silent when there is no pool count to divide by. */
    const checkedShare = useMemo(() => (
        poolTotal > 0 ? `${Math.round((totals.checked / poolTotal) * 100)}% of the pool` : undefined
    ), [poolTotal, totals.checked]);

    const columns: ColumnDef<RuleRow>[] = useMemo(() => [
        {
            key: 'rule',
            header: 'Rule',
            sortable: true,
            sortValue: r => r.ruleId,
            hideable: false,
            render: r => <span className="qq-rulecell"><b>{r.ruleId}</b></span>,
        },
        {
            key: 'pass',
            header: 'Pass',
            sortable: true,
            sortValue: r => r.pass,
            render: r => <span style={{ color: verdictColor.pass }}>{r.pass}</span>,
        },
        {
            key: 'unsure',
            header: 'Unsure',
            sortable: true,
            sortValue: r => r.unsure,
            render: r => <span style={{ color: r.unsure ? verdictColor.unsure : '#bbb' }}>{r.unsure}</span>,
        },
        {
            key: 'fail',
            header: 'Fail',
            sortable: true,
            // Severity first, then count: eight minor fails are not a worse rule
            // than one critical, and this list is read worst-first.
            sortValue: r => (r.failBySeverity.critical * 1e6) + (r.failBySeverity.major * 1e3) + r.fail,
            render: r => (
                <span style={{ color: r.fail ? verdictColor.fail : '#bbb', fontWeight: r.fail ? 700 : 400 }}>{r.fail}</span>
            ),
        },
        {
            key: 'severity',
            header: 'Fails (crit/maj/min)',
            sortable: true,
            sortValue: r => (['critical', 'major', 'minor'] as const)
                .reduce((n, sev) => n + r.failBySeverity[sev] * 10 ** (sevRank[sev] * 3), 0),
            render: r => <SevPills c={r.failBySeverity} />,
        },
    ], []);

    return (
        <>
            {/*
              * Two rows, because there are two populations and conflating them
              * overstates the problem by roughly the number of rules: ten rules
              * judge one question, so 170 fails is nothing like 170 bad questions.
              * The first counts QUESTIONS, each in exactly one bucket worst-first;
              * the second counts the VERDICTS behind them.
              */}
            <h3 className="qq-statsheading">Questions</h3>
            <ScoreCards items={[
                {
                    label: 'In scope',
                    // 0 from a failed count reads as "this subject has no
                    // questions", which is a claim; a dash says we do not know.
                    value: poolTotal || undefined,
                    fallback: '—',
                    comment: poolScope,
                },
                { label: 'Checked', value: totals.checked, comment: checkedShare },
                { label: 'Clean', value: totals.clean, perf: 'high' },
                { label: 'Critical', value: totals.critical, perf: 'low' },
                { label: 'Major', value: totals.major, perf: 'low' },
                { label: 'Minor', value: totals.minor, perf: 'med' },
                { label: 'Unsure', value: totals.unsure, perf: 'neutral' },
            ]} className="qq-stats" />

            <h3 className="qq-statsheading">Verdicts</h3>
            <ScoreCards items={[
                { label: 'Total', value: verdicts.total },
                { label: 'Pass', value: verdicts.pass, perf: 'high' },
                { label: 'Fail · critical', value: verdicts.critical, perf: 'low' },
                { label: 'Fail · major', value: verdicts.major, perf: 'low' },
                { label: 'Fail · minor', value: verdicts.minor, perf: 'med' },
                { label: 'Unsure', value: verdicts.unsure, perf: 'neutral' },
            ]} className="qq-stats" />

            <DataTable
                columns={columns}
                data={ruleRows}
                rowKey={r => r.ruleId}
                compact
                bare
                sort={sort}
                onSortChange={setSort}
                emptyMessage="No rule has been run over the questions in scope."
                /* One expansion holding both halves: what the rule ASKS, and what
                   it found. */
                renderExpandedRow={r => (
                    <>
                        {r.info && <RuleRubric info={r.info} />}
                        <RuleFindings rule={r.ruleId} subject={subject} filters={filters} />
                    </>
                )}
            />
        </>
    );
}
